import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Divider,
  alpha,
} from "@mui/material";
import axios from "axios";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { useForm } from "react-hook-form";
import { showToast, WEBSERVICE } from "../../../CoreDataService";
import earnBanner from "../../../Assets/Images/earn-banner.png";
import { useSelector } from "react-redux";
import { ContainedButton } from "../../../Common/Buttons";

const EarnInterest = () => {
  const { btncolor, brokerBasicInfo } = useSelector((state) => state);
  const [assets, setAssets] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [investmentDurations, setInvestmentDurations] = useState([]);
  const [investmentDetails, setInvestmentDetails] = useState([]);
  const [currencyIdSelectedByUser, setCurrencyIdSelectedByUser] =
    useState(null);
  const [investmentType, setInvestmentType] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [listsOfAssets, setListsOfAssets] = useState([]);
  const [autoSubscribe, setAutoSubscribe] = useState(0);
  const [isSubscribeModalOpen, setIsSubscribeModalOpen] = useState(false);
  const [durationSelectedByUser, setDurationSelectedByUser] = useState({});
  const [currencyNameSelectedByUser, setCurrencyNameSelectedByUser] =
    useState("");
  const [
    availableBalanceOfSelectedCurrency,
    setAvailableBalanceOfSelectedCurrency,
  ] = useState(0);
  const [selectedCurrencyDurationList, setSelectedCurrencyDurationList] =
    useState([]);

  const ASSET_ICON_BASE_URL =
    "https://brokersexchange.s3.us-west-1.amazonaws.com/currency_logo/";

  const {
    register,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });

  const oneTime = watch("oneTimeInvestmentAmount");
  const daily = watch("dailyInvestmentAmount");
  const uuid = localStorage.getItem("uuid");

  useEffect(() => {
    fetchAssets();
    fetchDurations();
    fetchUserExistingInvestment();
  }, []);

  const fetchDurations = async () => {
    try {
      const res = await axios.get(
        WEBSERVICE + "crypto/banking/earn/getEarnDurations",
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );
      if (res.data.error?.error_data !== 0) {
        showToast("error", res.data.error.error_msg);
      } else {
        setInvestmentDurations(res.data.investmentDurations);
      }
    } catch (err) {
      showToast("error", "Error fetching durations:");
    }
  };

  const getDurationDetails = (id) =>
    investmentDurations.find((d) => d.id === id || d.duration === id);

  const fetchAssets = async () => {
    try {
      const res = await axios.get(
        `${WEBSERVICE}crypto/banking/earn/getBankAssets?userUuid=${uuid}`,
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );
      if (res.data.error?.error_data !== 0) {
        showToast("error", res.data.error.error_msg);
        return;
      }
      const grouped = res.data.bankAssets.reduce((acc, item) => {
        const existing = acc.find((a) => a.currency === item.currency);
        const duration = {
          id: item.id,
          durationId: item.durationId,
          interestRate: item.interestRate,
        };
        if (existing) {
          existing.durations.push(duration);
        } else {
          acc.push({
            currencyId: item.currencyId,
            currency: item.currency,
            isActive: item.isActive,
            durations: [duration],
          });
        }
        return acc;
      }, []);
      setListsOfAssets(grouped);
    } catch (err) {
      if (err.response?.status === 401)
        showToast("error", "Session expired. Please log in again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserExistingInvestment = async () => {
    try {
      const res = await axios.post(
        WEBSERVICE + "crypto/banking/earn/getEarnInterestsByUser",
        JSON.stringify({ uuid: localStorage.getItem("uuid") }),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );
      if (res.data.error?.error_data !== 0) {
        showToast("error", res.data.error.error_msg);
      } else {
        setInvestmentDetails(res.data.investmentDetails || []);
      }
    } catch (err) {
      if (err.response?.status === 401)
        showToast("warning", "SESSION TIMED OUT. LOGIN AGAIN");
    }
  };

  const isAlreadyInvested = (currency) => {
    const item = investmentDetails.find((inv) => inv.asset === currency);
    return item?.isFundAllocated === 1;
  };

  const handleSelect = (currencyId, durationId) => {
    setSelected((prev) => ({ ...prev, [currencyId]: durationId }));
  };

  const getSelectedCurrencyAllDurations = (currencyId) => {
    const matchedAsset = listsOfAssets.find(
      (asset) => asset.currencyId === currencyId
    );
    setSelectedCurrencyDurationList(matchedAsset?.durations || {});
  };

  const handleSubscribeButtonClick = (currencyId, currencyName) => {
    setCurrencyIdSelectedByUser(currencyId);
    setCurrencyNameSelectedByUser(currencyName);
    getSelectedCurrencyAllDurations(currencyId);
    setAutoSubscribe(0);
    if (selected[currencyId]) {
      setDurationSelectedByUser(selected[currencyId]);
      getAvailableBalanceOfSelectedCurrency(currencyId);
      setIsSubscribeModalOpen(true);
    } else {
      showToast("info", "Please select a duration");
    }
  };

  const getAvailableBalanceOfSelectedCurrency = async (currencyId) => {
    try {
      const response = await axios.post(
        `${WEBSERVICE}transaction/getUserBalance`,
        JSON.stringify({
          customerId: localStorage.getItem("user_id"),
          uuid: localStorage.getItem("uuid"),
        }),
        {
          headers: {
            "Content-Type": "application/json",
            authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );
      if (response.data.error.error_data !== 0) {
        showToast("danger", "Cannot fetch user balance");
      } else {
        const match = response.data.userBalanceList?.find(
          (item) => item.currencyId === currencyId
        );
        if (match) setAvailableBalanceOfSelectedCurrency(match.closingBalance);
      }
    } catch (error) {
      if (error.response?.status === 401)
        showToast("warning", "SESSION TIMED OUT. LOGIN AGAIN");
    }
  };

  const handleEarnInterestRequest = (param) => {
    if (param === "3") {
      setInvestmentType("3");
    } else {
      if (daily !== "") {
        setInvestmentType("2");
      } else {
        setInvestmentType("1");
      }
    }
    setIsConfirmModalOpen(true);
  };

  const handleDurationSelectionForAsset = (duration, currencyId) => {
    setDurationSelectedByUser(duration.durationId);
    setCurrencyIdSelectedByUser(currencyId);
  };

  const validateMinimum = (value) => {
    if (!value) return true;
    return parseFloat(value) >= 0.0001 || "Amount should be at least 0.0001";
  };

  const handleMutualClear = (field) => {
    if (field === "oneTimeInvestmentAmount")
      setValue("dailyInvestmentAmount", "", { shouldValidate: true });
    else if (field === "dailyInvestmentAmount")
      setValue("oneTimeInvestmentAmount", "", { shouldValidate: true });
  };

  const confirmInvestment = () => {
    if (investmentType == "3") createInvestmentPayloadWithFullWallet();
    else createInvestmentPayloadWithOutFullWallet();
  };

  const createInvestmentPayloadWithOutFullWallet = () => {
    let investment = {
      uuid: localStorage.getItem("uuid"),
      durationId: durationSelectedByUser,
      currencyId: currencyIdSelectedByUser,
      autoInvest: autoSubscribe,
      invTypeId: investmentType,
    };
    if (daily != "") {
      investment["amount"] = daily;
      investment["autoInvest"] = 1;
    } else {
      investment["amount"] = oneTime;
      investment["autoInvest"] = 0;
    }
    handleCreateInvestment({ investment });
  };

  const createInvestmentPayloadWithFullWallet = () => {
    handleCreateInvestment({
      investment: {
        uuid: localStorage.getItem("uuid"),
        durationId: durationSelectedByUser,
        currencyId: currencyIdSelectedByUser,
        invTypeId: "3",
        autoInvest: 1,
      },
    });
  };

  const handleCreateInvestment = async (payload) => {
    setLoading(true);
    document.body.classList.add("overlay");
    try {
      const response = await axios.post(
        `${WEBSERVICE}crypto/banking/earn/createEarnInterest`,
        JSON.stringify(payload),
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "BEARER " + localStorage.getItem("access_token"),
          },
        }
      );
      const result = response.data;
      if (result.error.error_data !== 0) {
        showToast("error", result.error.error_msg);
      } else {
        showToast("success", result.error.error_msg);
        fetchUserExistingInvestment();
        fetchDurations();
        fetchAssets();
      }
    } catch (error) {
      showToast("error", "Could Not Connect To Server");
    } finally {
      setLoading(false);
      setValue("dailyInvestmentAmount", "", { shouldValidate: true });
      setValue("oneTimeInvestmentAmount", "", { shouldValidate: true });
    }
  };

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={pageWrapper}>
        <Skeleton
          variant="rounded"
          height={220}
          sx={{ borderRadius: "20px", mb: 3 }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
              lg: "repeat(4,1fr)",
            },
            gap: 2,
          }}
        >
          {[...Array(4)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={240}
              sx={{ borderRadius: "16px" }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <Box sx={pageWrapper}>
      {/* ── Hero Banner ── */}
      <Box
        sx={{
          ...heroBanner,
          background: `linear-gradient(135deg, ${alpha(
            btncolor || "#1976d2",
            0.97
          )} 0%, ${alpha(btncolor || "#1976d2", 0.72)} 100%)`,
        }}
      >
        {/* Decorative circles */}
        <Box sx={heroDecorCircleLg} />
        <Box sx={heroDecorCircleSm} />

        <Box sx={heroLayout}>
          {/* Left: text */}
          <Box sx={heroLeft}>
            <Box sx={heroBadge}>
              <TrendingUpOutlinedIcon sx={{ fontSize: 13, color: "#fff" }} />
              <Typography sx={heroBadgeText}>Crypto Earn</Typography>
            </Box>
            <Typography sx={heroTitle}>
              {brokerBasicInfo?.exchange || ""} Earn Interest
            </Typography>
            <Typography sx={heroSubtitle}>
              You may sleep, but your crypto won't!
              <br />
              Make your idle crypto work harder for you.
            </Typography>

            {/* Feature rows */}
          </Box>

          {/* Right: image */}
          {/* <Box sx={heroRight}>
            <Box
              component="img"
              src={earnBanner}
              alt="Earn Interest"
              sx={heroImage}
            />
          </Box> */}
        </Box>
      </Box>

      {/* <Box sx={heroFeatureList}>
        {[
          {
            icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
            text: "Same tokens returned — interest paid in kind",
          },
          {
            icon: <ShieldOutlinedIcon sx={{ fontSize: 16 }} />,
            text: "Flexible and locked durations available",
          },
          {
            icon: <AutorenewIcon sx={{ fontSize: 16 }} />,
            text: "Auto-invest to compound your earnings",
          },
        ].map((f, i) => (
          <Box key={i} sx={heroFeatureRow}>
            <Box sx={heroFeatureIcon}>{f.icon}</Box>
            <Typography sx={heroFeatureText}>{f.text}</Typography>
          </Box>
        ))}
      </Box> */}

      {/* ── Available Assets ── */}
      <Box sx={{ mb: 3 }}>
        {/* Section heading */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            mb: 2.5,
          }}
        >
          <Box>
            <Typography sx={secTitle}>Available Assets</Typography>
            <Typography sx={secCaption}>
              Select a duration and start earning on your idle crypto
            </Typography>
          </Box>
          <Typography sx={secCount}>
            {listsOfAssets.length} Product
            {listsOfAssets.length !== 1 ? "s" : ""}
          </Typography>
        </Box>

        {listsOfAssets.length > 0 ? (
          <Box sx={cardGrid}>
            {listsOfAssets.map((asset) => {
              const selectedDurationId = selected[asset.currencyId];
              const selectedDuration = asset.durations.find(
                (d) => d.durationId === selectedDurationId
              );
              const apr = selectedDuration?.interestRate ?? null;
              const alreadyInvested = isAlreadyInvested(asset.currency);

              return (
                <Box key={asset.currencyId} sx={productCard}>
                  {/* Gradient top strip */}
                  <Box
                    sx={{
                      ...cardTopStrip,
                      background: `linear-gradient(90deg, ${alpha(
                        btncolor || "#1976d2",
                        0.9
                      )}, ${alpha(btncolor || "#1976d2", 0.4)})`,
                    }}
                  />

                  {/* Card body */}
                  <Box sx={cardBody}>
                    {/* Coin + name row */}
                    <Box sx={coinRow}>
                      <Box sx={coinRing}>
                        <img
                          src={`${ASSET_ICON_BASE_URL}${asset.currency}.png`}
                          alt={asset.currency}
                          width={40}
                          height={40}
                          style={{ borderRadius: "50%", display: "block" }}
                        />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={coinTicker}>
                          {asset.currency}
                        </Typography>
                        <Typography sx={coinLabel}>Crypto Earn</Typography>
                      </Box>
                      {alreadyInvested && (
                        <Box sx={activePill}>
                          <CheckCircleOutlineIcon sx={{ fontSize: 11 }} />
                          Active
                        </Box>
                      )}
                    </Box>

                    {/* APR highlight box */}
                    <Box sx={aprBox}>
                      <Typography sx={aprBoxLabel}>
                        Est. Annual Return
                      </Typography>
                      <Typography
                        sx={{
                          ...aprBig,
                          color: apr ? "#16a34a" : "var(--text-color)",
                          opacity: apr ? 1 : 0.3,
                        }}
                      >
                        {apr ? `${apr}%` : "—"}
                      </Typography>
                      {apr && (
                        <Box sx={aprTag}>
                          <AutorenewIcon sx={{ fontSize: 11 }} />
                          APR
                        </Box>
                      )}
                    </Box>

                    {/* Duration segment control */}
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={durationHeading}>
                        Lock-in Period
                      </Typography>
                      <Box sx={segmentWrap}>
                        {asset.durations.map((d) => {
                          const isSel = selectedDurationId === d.durationId;
                          return (
                            <Box
                              key={d.durationId}
                              onClick={() =>
                                handleSelect(asset.currencyId, d.durationId)
                              }
                              sx={{
                                ...segmentItem,
                                backgroundColor: isSel
                                  ? btncolor
                                  : "transparent",
                                color: "var(--text-color)",
                                fontWeight: isSel ? 700 : 500,
                                opacity: isSel ? 1 : 0.55,
                                boxShadow: isSel
                                  ? "0 2px 8px rgba(0,0,0,0.18)"
                                  : "none",
                                "&:hover": {
                                  opacity: 1,
                                  backgroundColor: isSel
                                    ? btncolor
                                    : alpha(btncolor || "#1976d2", 0.12),
                                },
                              }}
                            >
                              {getDurationDetails(d.durationId)?.duration ||
                                d.durationId}
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.58rem",
                                  display: "block",
                                  opacity: 0.7,
                                  lineHeight: 1,
                                }}
                              >
                                days
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    {/* CTA */}
                    {alreadyInvested ? (
                      <Box sx={allocatedBanner}>
                        <CheckCircleOutlineIcon
                          sx={{ fontSize: 16, color: "#16a34a" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: "#16a34a",
                          }}
                        >
                          Funds Allocated
                        </Typography>
                      </Box>
                    ) : (
                      <ContainedButton
                        fullWidth
                        variant="contained"
                        sx={cardEarnBtn}
                        onClick={() =>
                          handleSubscribeButtonClick(
                            asset.currencyId,
                            asset.currency
                          )
                        }
                      >
                        Earn Interest
                      </ContainedButton>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box sx={emptyState}>
            <InboxOutlinedIcon sx={emptyIcon} />
            <Typography sx={emptyText}>
              No assets available at the moment
            </Typography>
          </Box>
        )}
      </Box>

      {/* ── Subscribe Modal ── */}
      <Dialog
        open={isSubscribeModalOpen}
        onClose={() => setIsSubscribeModalOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <img
              src={`${ASSET_ICON_BASE_URL}${currencyNameSelectedByUser.toUpperCase()}.png`}
              width="20"
              height="20"
              alt={currencyNameSelectedByUser}
              style={{ borderRadius: "50%" }}
            />
            Earn Interest on {currencyNameSelectedByUser}
          </Box>
          <IconButton
            onClick={() => setIsSubscribeModalOpen(false)}
            size="small"
            sx={dialogCloseBtn}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />

        <DialogContent sx={dialogContentStyle}>
          {/* Duration + balance row */}
          <Box sx={modalTopRow}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={dialogSectionLabel}>Lock-in Period</Typography>
              <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                {durationSelectedByUser === 1 ? (
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      ...durationBtn,
                      bgcolor: btncolor,
                      borderColor: btncolor,
                    }}
                    onClick={() =>
                      handleDurationSelectionForAsset(
                        durationSelectedByUser,
                        currencyIdSelectedByUser
                      )
                    }
                  >
                    {getDurationDetails(durationSelectedByUser)?.duration}
                  </Button>
                ) : (
                  selectedCurrencyDurationList.map(
                    (duration) =>
                      duration.durationId !== 1 && (
                        <Button
                          key={duration.durationId}
                          size="small"
                          variant={
                            durationSelectedByUser === duration.durationId
                              ? "contained"
                              : "outlined"
                          }
                          sx={{
                            ...durationBtn,
                            bgcolor:
                              durationSelectedByUser === duration.durationId
                                ? btncolor
                                : "transparent",
                            borderColor:
                              durationSelectedByUser === duration.durationId
                                ? btncolor
                                : "rgba(125,125,125,0.35)",
                            color: "var(--text-color)",
                          }}
                          onClick={() =>
                            handleDurationSelectionForAsset(
                              duration,
                              currencyIdSelectedByUser
                            )
                          }
                        >
                          {getDurationDetails(duration.durationId)?.duration}
                        </Button>
                      )
                  )
                )}
              </Box>
            </Box>
            <Box sx={inlineBalanceBox}>
              <Typography sx={balanceLabel}>Available</Typography>
              <Typography sx={balanceValue}>
                {availableBalanceOfSelectedCurrency}{" "}
                <span
                  style={{
                    opacity: 0.55,
                    fontWeight: 400,
                    fontSize: "0.78rem",
                  }}
                >
                  {currencyNameSelectedByUser}
                </span>
              </Typography>
            </Box>
          </Box>

          {durationSelectedByUser !== 1 && (
            <Typography sx={{ ...dialogInfoText, mb: 2 }}>
              If you choose to redeem early, your assets will earn the
              corresponding flexible product interest.
            </Typography>
          )}

          <Divider sx={{ borderColor: "rgba(125,125,125,0.12)", mb: 2.5 }} />

          {/* ── Option 1: One-time ── */}
          <Box sx={investSection}>
            <Box sx={investSectionHeader}>
              <Box
                sx={{
                  ...investSectionNum,
                  backgroundColor: alpha(btncolor || "#1976d2", 0.12),
                  color: btncolor,
                }}
              >
                1
              </Box>
              <Typography sx={investBoxTitle}>One-time Investment</Typography>
              <Typography sx={investSectionTag}>No Auto-invest</Typography>
            </Box>
            <TextField
              label="One Time Investment Amount"
              fullWidth
              type="number"
              size="small"
              {...register("oneTimeInvestmentAmount", {
                onChange: () => handleMutualClear("oneTimeInvestmentAmount"),
                validate: validateMinimum,
              })}
              placeholder={
                durationSelectedByUser === 1
                  ? "Enter amount"
                  : "Enter amount for this lock-in period"
              }
              error={!!errors.oneTimeInvestmentAmount}
              helperText={errors.oneTimeInvestmentAmount?.message}
              sx={dialogTextField}
            />
            <Typography sx={{ ...dialogInfoText, mt: 1 }}>
              {durationSelectedByUser === 1
                ? "Moved once from your Spot Wallet to Earn Wallet immediately, invested at the next UTC 12:00 AM."
                : "Moved from Spot Wallet to Earn Wallet immediately and invested at the next UTC 12:00 AM for this lock-in period only. Upon expiry, assets auto-switch to flexible interest."}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(125,125,125,0.12)", my: 2 }} />

          {/* ── Option 2: Daily auto-invest ── */}
          <Box sx={investSection}>
            <Box sx={investSectionHeader}>
              <Box
                sx={{
                  ...investSectionNum,
                  backgroundColor: alpha(btncolor || "#1976d2", 0.12),
                  color: btncolor,
                }}
              >
                2
              </Box>
              <Typography sx={investBoxTitle}>
                {durationSelectedByUser === 1
                  ? "Daily Auto-invest"
                  : "Auto-invest (same lock-in on expiry)"}
              </Typography>
            </Box>
            <TextField
              label="Daily Investment Amount"
              fullWidth
              type="number"
              size="small"
              {...register("dailyInvestmentAmount", {
                onChange: () => handleMutualClear("dailyInvestmentAmount"),
                validate: validateMinimum,
              })}
              placeholder={
                durationSelectedByUser === 1
                  ? "Amount to auto-invest daily"
                  : "Amount to auto-invest now and on every expiry"
              }
              error={!!errors.dailyInvestmentAmount}
              helperText={errors.dailyInvestmentAmount?.message}
              sx={dialogTextField}
            />
            <Typography sx={{ ...dialogInfoText, mt: 1 }}>
              {durationSelectedByUser === 1
                ? "Moved from Spot Wallet to Earn Wallet every day at UTC 12:00 AM. Toggle off auto-invest anytime from your Earn Wallet."
                : "Moved at UTC 12:00 AM and re-invested at each expiry. Previous amount + interest shifts to flexible interest. Toggle off anytime."}
            </Typography>
            <Typography
              sx={{ ...dialogInfoText, fontWeight: 700, mt: 1, opacity: 0.85 }}
            >
              When turned off:{" "}
            </Typography>
            <Typography sx={dialogInfoText}>
              (1) Stops future auto-investing.{"  "}
              {durationSelectedByUser === 1
                ? "(2) Invested assets remain in Earn Wallet earning flexible interest."
                : "(2) Invested assets continue earning until lock-in expiry, then switch to flexible interest."}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(125,125,125,0.12)", my: 2 }} />

          {/* ── Option 3: Full wallet auto-invest ── */}
          <Box sx={investSection}>
            <Box sx={investSectionHeader}>
              <Box
                sx={{
                  ...investSectionNum,
                  backgroundColor: alpha(btncolor || "#1976d2", 0.12),
                  color: btncolor,
                }}
              >
                3
              </Box>
              <Typography sx={investBoxTitle}>
                {durationSelectedByUser === 1
                  ? "Auto-invest Full Wallet"
                  : "Auto-invest Full Wallet (this lock-in)"}
              </Typography>
            </Box>
            <Typography sx={{ ...dialogInfoText, mb: 1.5 }}>
              {durationSelectedByUser === 1
                ? "Your full wallet balance is moved to Earn Wallet daily at UTC 12:00 AM and invested automatically."
                : "Full balance moves at UTC 12:00 AM and re-invests at each lock-in expiry. Toggle off anytime from Earn Wallet."}
            </Typography>
            <ContainedButton
              variant="contained"
              size="small"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.82rem",
              }}
              onClick={() => {
                handleEarnInterestRequest("3");
                setIsSubscribeModalOpen(false);
              }}
            >
              Enable Full Wallet Auto-invest
            </ContainedButton>
          </Box>
        </DialogContent>

        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogActions
          sx={{ p: 2.5, backgroundColor: "var(--popupbody-color)" }}
        >
          <ContainedButton
            fullWidth
            variant="contained"
            disabled={
              !isValid ||
              (parseFloat(oneTime || 0) < 0.0001 &&
                parseFloat(daily || 0) < 0.0001)
            }
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              height: 44,
            }}
            onClick={() => {
              handleEarnInterestRequest("1");
              setIsSubscribeModalOpen(false);
            }}
          >
            Earn Interest
          </ContainedButton>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Modal ── */}
      <Dialog
        open={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaper }}
      >
        <DialogTitle sx={dialogTitleStyle}>
          Earn Interest
          <IconButton
            onClick={() => setIsConfirmModalOpen(false)}
            size="small"
            sx={dialogCloseBtn}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogContent sx={{ ...dialogContentStyle, pt: 3 }}>
          <Typography
            sx={{ ...dialogInfoText, fontSize: "0.9rem", opacity: 0.85 }}
          >
            By clicking Earn Interest, you agree to move the corresponding
            assets (including interests and newly purchased assets) in Spot
            Wallet to Earn Wallet as per the selection(s) made by you. You will
            find your Earn history under History &gt; Earn History and Earn
            Wallet under Wallet &gt; Earn Wallet.
          </Typography>
        </DialogContent>
        <Divider sx={{ borderColor: "rgba(125,125,125,0.15)" }} />
        <DialogActions
          sx={{ p: 2.5, backgroundColor: "var(--popupbody-color)" }}
        >
          <ContainedButton
            variant="contained"
            fullWidth
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              height: 44,
            }}
            onClick={() => {
              setIsConfirmModalOpen(false);
              confirmInvestment();
            }}
          >
            I Understand
          </ContainedButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ── Styles (mirrors Report.js conventions) ───────────────────────────────────

const pageWrapper = {
  px: { xs: 2, md: 4 },
  py: { xs: 2, md: 3 },
  backgroundColor: "var(--body-color)",
  color: "var(--text-color)",
  minHeight: "calc(100vh - 64px)",
};

// ── Hero ─────────────────────────────────────────────────────────────────────

const heroBanner = {
  borderRadius: "20px",
  mb: 3,
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 12px 40px rgba(0,0,0,0.22)",
};

const heroDecorCircleLg = {
  position: "absolute",
  width: 420,
  height: 420,
  borderRadius: "50%",
  backgroundColor: "rgba(255,255,255,0.06)",
  right: -80,
  top: -120,
  pointerEvents: "none",
};

const heroDecorCircleSm = {
  position: "absolute",
  width: 200,
  height: 200,
  borderRadius: "50%",
  backgroundColor: "rgba(255,255,255,0.05)",
  left: -40,
  bottom: -60,
  pointerEvents: "none",
};

const heroLayout = {
  display: "flex",
  alignItems: "center",
  gap: { xs: 2, md: 4 },
  px: { xs: 3, md: 5 },
  py: { xs: 3.5, md: 4 },
  position: "relative",
  zIndex: 1,
};

const heroLeft = {
  flex: 1,
  minWidth: 0,
};

const heroBadge = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.6,
  backgroundColor: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.22)",
  borderRadius: "20px",
  px: 1.5,
  py: 0.4,
  mb: 1.75,
};

const heroBadgeText = {
  fontSize: "0.7rem",
  fontWeight: 700,
  color: "#fff",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
};

const heroTitle = {
  fontSize: { xs: "1.5rem", md: "2rem" },
  fontWeight: 800,
  color: "#fff",
  lineHeight: 1.15,
  letterSpacing: "-0.02em",
  mb: 1,
};

const heroSubtitle = {
  fontSize: { xs: "0.85rem", md: "0.95rem" },
  color: "rgba(255,255,255,0.82)",
  lineHeight: 1.6,
  mb: 2.5,
};

const heroFeatureList = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

const heroFeatureRow = {
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const heroFeatureIcon = {
  color: "rgba(255,255,255,0.9)",
  display: "flex",
  flexShrink: 0,
};

const heroFeatureText = {
  fontSize: "0.82rem",
  color: "rgba(255,255,255,0.85)",
  lineHeight: 1.5,
};

const heroRight = {
  flexShrink: 0,
  display: { xs: "none", md: "flex" },
  alignItems: "center",
  justifyContent: "center",
};

const heroImage = {
  width: 200,
  filter: "drop-shadow(0 8px 24px rgba(0,0,0,0.25))",
};

// ── Available Assets — Premium Cards ─────────────────────────────────────────

const secTitle = {
  fontSize: "1.05rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const secCaption = {
  fontSize: "0.78rem",
  color: "var(--text-color)",
  opacity: 0.5,
  mt: 0.25,
};

const secCount = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "var(--text-color)",
  opacity: 0.4,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
};

const cardGrid = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "1fr 1fr",
    lg: "repeat(3, 1fr)",
    xl: "repeat(4, 1fr)",
  },
  gap: 2,
};

const productCard = {
  backgroundColor: "var(--body-color-less)",
  border: "1px solid rgba(125,125,125,0.13)",
  borderRadius: "18px",
  overflow: "hidden",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
  },
};

const cardTopStrip = {
  height: 5,
  width: "100%",
};

const cardBody = {
  p: 2.5,
  display: "flex",
  flexDirection: "column",
  gap: 0,
};

const coinRow = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  mb: 2,
};

const coinRing = {
  width: 48,
  height: 48,
  borderRadius: "50%",
  border: "2px solid rgba(125,125,125,0.18)",
  backgroundColor: "var(--body-color-less2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  overflow: "hidden",
};

const coinTicker = {
  fontSize: "1rem",
  fontWeight: 800,
  color: "var(--text-color)",
  letterSpacing: "0.01em",
  lineHeight: 1.2,
};

const coinLabel = {
  fontSize: "0.68rem",
  color: "var(--text-color)",
  opacity: 0.4,
  fontWeight: 500,
};

const activePill = {
  ml: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.35,
  fontSize: "0.62rem",
  fontWeight: 700,
  color: "#16a34a",
  backgroundColor: "rgba(22,163,74,0.1)",
  border: "1px solid rgba(22,163,74,0.28)",
  borderRadius: "20px",
  px: 0.9,
  py: 0.3,
  flexShrink: 0,
};

const aprBox = {
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "12px",
  px: 2,
  py: 1.75,
  mb: 2,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  position: "relative",
  overflow: "hidden",
};

const aprBoxLabel = {
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "var(--text-color)",
  opacity: 0.4,
  mb: 0.5,
};

const aprBig = {
  fontSize: "2.2rem",
  fontWeight: 900,
  lineHeight: 1,
  letterSpacing: "-0.03em",
};

const aprTag = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.3,
  mt: 0.5,
  fontSize: "0.62rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#16a34a",
  backgroundColor: "rgba(22,163,74,0.1)",
  borderRadius: "20px",
  px: 0.9,
  py: 0.25,
};

const durationHeading = {
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.09em",
  color: "var(--text-color)",
  opacity: 0.4,
  mb: 1,
};

const segmentWrap = {
  display: "flex",
  gap: 0.5,
  backgroundColor: "var(--body-color-less2)",
  borderRadius: "10px",
  p: 0.5,
};

const segmentItem = {
  flex: 1,
  textAlign: "center",
  py: 0.75,
  px: 0.5,
  borderRadius: "8px",
  fontSize: "0.78rem",
  cursor: "pointer",
  userSelect: "none",
  transition: "all 0.15s ease",
  lineHeight: 1.3,
};

const allocatedBanner = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  py: 1.1,
  borderRadius: "10px",
  backgroundColor: "rgba(22,163,74,0.08)",
  border: "1px solid rgba(22,163,74,0.2)",
};

const cardEarnBtn = {
  height: 44,
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.88rem",
  letterSpacing: "0.01em",
};

const emptyState = {
  textAlign: "center",
  py: 8,
};

const emptyIcon = {
  fontSize: 52,
  color: "var(--text-color)",
  opacity: 0.3,
  mb: 1,
};

const emptyText = {
  fontSize: "0.92rem",
  color: "var(--text-color)",
  opacity: 0.55,
};

// Dialog
const dialogPaper = {
  borderRadius: "16px",
  minWidth: { xs: "90%", sm: 480 },
  boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
  overflow: "hidden",
  backgroundColor: "var(--popupbody-color)",
  color: "var(--text-color)",
};

const dialogTitleStyle = {
  textAlign: "center",
  fontWeight: 700,
  fontSize: "1.05rem",
  py: 2,
  color: "var(--text-color)",
  backgroundColor: "var(--popupheader-color)",
  position: "relative",
};

const dialogCloseBtn = {
  position: "absolute",
  right: 12,
  top: "50%",
  transform: "translateY(-50%)",
  color: "var(--text-color)",
  opacity: 0.6,
};

const dialogContentStyle = {
  backgroundColor: "var(--popupbody-color)",
  py: 2.5,
  px: { xs: 2, md: 3 },
};

const dialogSectionLabel = {
  fontSize: "0.72rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--text-color)",
  opacity: 0.5,
};

const dialogInfoText = {
  fontSize: "0.82rem",
  color: "var(--text-color)",
  opacity: 0.7,
  lineHeight: 1.65,
};

// Flat invest sections (no background box)
const investSection = {};

const investSectionHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1.25,
};

const investSectionNum = {
  width: 22,
  height: 22,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.7rem",
  fontWeight: 800,
  flexShrink: 0,
};

const investSectionTag = {
  fontSize: "0.65rem",
  fontWeight: 600,
  color: "var(--text-color)",
  opacity: 0.4,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  ml: "auto",
};

const investBoxTitle = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const dialogTextField = {
  "& .MuiInputBase-root": {
    borderRadius: "10px",
    backgroundColor: "var(--body-color-less2)",
    color: "var(--text-color)",
    fontSize: "0.88rem",
  },
  "& .MuiInputBase-input": { color: "var(--text-color)" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(125,125,125,0.25)",
  },
  "& .MuiInputLabel-root": { color: "var(--text-color)", opacity: 0.6 },
};

// Inline balance (top-right of modal)
const modalTopRow = {
  display: "flex",
  alignItems: "flex-start",
  gap: 2,
  mb: 2,
  flexWrap: "wrap",
};

const inlineBalanceBox = {
  textAlign: "right",
  flexShrink: 0,
};

const balanceLabel = {
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--text-color)",
  opacity: 0.45,
};

const balanceValue = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "var(--text-color)",
  mt: 0.25,
};

const durationBtn = {
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "8px",
  minWidth: 52,
  height: 30,
  color: "var(--text-color)",
};

export default EarnInterest;
